import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Clock, Flame, ChevronRight, MoreHorizontal, Shuffle, RefreshCw, Users, Lock, ShieldCheck } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FavoriteButton } from "@/components/FavoriteButton";
import { PlanEatButton } from "@/components/journal/PlanEatButton";

export function MealCard({ meal, day, memberId, paid, busy, onAlternatives, onRegenerate, onPortions }) {
  const { t, tv } = useLang();
  const nut = meal.nutrition_by_member?.[memberId] || meal.nutrition;
  const portions = meal.servings?.[memberId];
  const isBusy = busy === `swap-${day}-${meal.slot}` || busy === `portions-${day}-${meal.slot}`;

  return (
    <div className="chenia-card overflow-hidden group relative" data-testid={`plan-meal-${meal.slot}`}>
      <Link to={`/recipe/${meal.recipe_id}`} className="block relative h-40">
        <img src={meal.image} alt="" style={{ objectPosition: meal.focal || "50% 50%" }} className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
        <span className="absolute top-3 left-3 rounded-full bg-background/90 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wide">{t(meal.slot)}</span>
        {meal.tier && meal.tier !== "free" && <span className={`absolute bottom-3 left-3 rounded-full px-2 py-0.5 text-[10px] font-bold ${meal.tier === "premium" ? "bg-primary text-primary-foreground" : "bg-chenia-sage text-foreground"}`} data-testid={`meal-tier-${meal.slot}`}>{t(`tier_${meal.tier}`)}</span>}
        {isBusy && <span className="absolute inset-0 grid place-items-center bg-background/60"><RefreshCw className="h-6 w-6 animate-spin text-primary" /></span>}
        <FavoriteButton recipeId={meal.recipe_id} size="sm" className="absolute bottom-3 right-3" />
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`meal-actions-${meal.slot}`} className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-background/90 backdrop-blur hover:bg-background transition-colors">
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-2xl min-w-52">
          <DropdownMenuItem onClick={onAlternatives} data-testid={`meal-alternatives-${meal.slot}`} className="gap-2 rounded-xl">
            <Shuffle className="h-4 w-4" /> {t("alternatives")} {!paid && <Lock className="h-3 w-3 ml-auto text-muted-foreground" />}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onRegenerate} data-testid={`meal-regenerate-${meal.slot}`} className="gap-2 rounded-xl">
            <RefreshCw className="h-4 w-4" /> {t("regen_meal")} {!paid && <Lock className="h-3 w-3 ml-auto text-muted-foreground" />}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onPortions} data-testid={`meal-portions-${meal.slot}`} className="gap-2 rounded-xl">
            <Users className="h-4 w-4" /> {t("adjust_portions")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="p-4">
        <h3 className="font-semibold leading-snug">{tv(meal.name)}</h3>
        {meal.variant && (
          <p className="mt-1 text-[11px] inline-flex items-center gap-1 rounded-full bg-chenia-sage/30 px-2 py-0.5" data-testid={`meal-variant-${meal.slot}`}>
            <ShieldCheck className="h-3 w-3" /> {t("variant_badge")} {meal.variant.avoided_allergens.map((a) => t(`al_${a}`).toLowerCase()).join(", ")}
          </p>
        )}
        <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Flame className="h-4 w-4" /> {nut ? `${nut.calories} ${t("kcal")}` : "—"}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {meal.total_time ?? meal.prep_time} {t("min")}</span>
          {portions != null && <span className="ml-auto text-xs rounded-full bg-secondary px-2 py-0.5" data-testid={`meal-portions-value-${meal.slot}`}>{portions} {portions > 1 ? t("portions") : t("portion")}</span>}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{nut ? `P ${nut.protein}g · G ${nut.carbs}g · L ${nut.fat}g` : t("nutrition_unavailable")}</span>
          <Link to={`/recipe/${meal.recipe_id}`} className="text-sm font-semibold text-primary inline-flex items-center gap-1 hover:gap-2 transition-all">{t("view_recipe")} <ChevronRight className="h-4 w-4" /></Link>
        </div>
        {portions != null && <PlanEatButton day={day} slot={meal.slot} memberId={memberId} />}
      </div>
    </div>
  );
}
